// Eskom PARCC — bulk-import parser/mapper (Task 23).
//
// The PURE, framework-free heart of the importer. No React, no Firebase: it
// reads an XLSX/CSV into a matrix, auto-detects the header row among leading
// junk, resolves columns through a faithful alias map, and turns each record
// into a transaction-upsert payload plus the finding inputs derived from the
// Initial/Updated Findings columns. Heavily unit-tested (importParse.test.ts).
//
// Parity source: git 6810c83:index.html —
//   • matrixToObjects  → header scoring + record building (detectHeaderRow /
//     matrixToRecords here)
//   • getField         → alias resolution (resolveField here, with the alias map
//     lifted faithfully from buildTransactionFromImport)
//   • buildTransactionFromImport / upsertFindingsForTransaction / processBulkRows
//     → rowToImport (transaction payload + findings, reference-or-skip)
//
// Reuse (not reimplemented): `inferDataset` (src/domain/dataset.ts), `parseRand`
// + `formatRand` (src/lib/money.ts), `classifyFinding` (src/domain/classify.ts),
// `normalizeStatusValue` + `firstNonBlank` (src/domain/normalize.ts), and the
// SheetJS `xlsx` library for reading both .xlsx and .csv.
import * as XLSX from 'xlsx';
import { inferDataset } from '../../domain/dataset';
import { parseRand, formatRand } from '../../lib/money';
import { classifyFinding } from '../../domain/classify';
import { normalizeGapStatus, firstNonBlank } from '../../domain/normalize';
import { IMPORT_UPDATE_COLUMNS } from '../../lib/importColumns';
import { normalisePeriod, normaliseFinancialYear } from '../../domain/fiscal';
import type { TransactionInsertInput, FindingInsertInput } from '../../lib/dbWrite';

/** A keyed spreadsheet row: header → cell text. */
export type ImportRecord = Record<string, string>;

/** What one mappable row becomes: a transaction upsert + its findings. */
export interface ImportRow {
  transaction: TransactionInsertInput;
  findings: FindingInsertInput[];
}

// --- Header normalisation + detection --------------------------------------

/**
 * Canonicalise a header/cell for matching: lowercase, collapse line breaks and
 * any run of non-alphanumerics to single spaces, trim. Ported verbatim from the
 * monolith's `normalizeHeader` so the alias map and scoring stay byte-identical.
 */
export function normalizeHeader(s: string | null | undefined): string {
  return String(s ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\n\r]+/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * The recognised header vocabulary used to score candidate header rows, lifted
 * from the monolith's `knownHeaders` list (already in normalised form).
 */
export const KNOWN_HEADERS: readonly string[] = [
  'dataset',
  'reference no',
  'reference',
  'contract number',
  'po number',
  'task order',
  'transaction reference',
  'year',
  'financial year',
  'period',
  'quarter',
  'division',
  'supplier',
  'csd number',
  'value',
  'value r',
  'mechanism',
  'link',
  'links',
  'gaps status',
  'status',
  'statu',
  'reviewed y n',
  'reviewed',
  'compliance level',
  'compliance level based on updated findings strict rule',
  'self declaration listing',
  'self-declaration listing',
  'initial findings',
  'updated findings',
  'findings',
  // R6: the population template's own columns. Without these a sheet whose
  // header row is mostly panel/link columns scores low and the detector can pick
  // the wrong row. (`normalizeHeader` folds the punctuation, so the raw
  // "Panel Release(Y/N)" and "OpenText link" both land on these forms.)
  'panel release y n',
  'opentext link',
  'link provided',
  'issue with links',
  'ou',
  'buyer name',
  'description of goods',
];

/** A loose keyword fallback for header scoring (monolith parity). */
const HEADER_KEYWORDS =
  /reference|contract|supplier|division|period|dataset|status|finding|compliance|value|mechanism|link/;

/** Score a candidate row by how header-like its cells are. */
function scoreHeaderRow(row: readonly string[]): number {
  return row.reduce((score, cell) => {
    const norm = normalizeHeader(cell);
    if (!norm) return score;
    if (KNOWN_HEADERS.includes(norm)) return score + 2;
    if (HEADER_KEYWORDS.test(norm)) return score + 1;
    return score;
  }, 0);
}

export interface DetectedHeader {
  headerIndex: number;
  headers: string[];
}

/**
 * Scan the first ~15 rows and pick the best-scoring header row. Ties keep the
 * earliest row; if nothing scores, row 0 is used. Blank header cells are named
 * positionally (`Column N`). Mirrors the monolith's `matrixToObjects` scoring.
 */
export function detectHeaderRow(rows: readonly (readonly string[])[]): DetectedHeader {
  let headerIndex = 0;
  let bestScore = -1;
  rows.slice(0, 15).forEach((row, idx) => {
    const score = scoreHeaderRow(row);
    if (score > bestScore) {
      bestScore = score;
      headerIndex = idx;
    }
  });
  // De-duplicate repeated header names (the new bulk-upload template has TWO
  // "Division" columns): the first occurrence keeps its name, later ones get a
  // numeric suffix, so header-keyed records never clobber the real value and
  // `resolveField` still resolves the canonical (first) column.
  const seen = new Map<string, number>();
  const headers = (rows[headerIndex] ?? []).map((h, idx) => {
    const base = String(h ?? '').trim() || `Column ${idx + 1}`;
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    return n > 1 ? `${base} ${n}` : base;
  });
  return { headerIndex, headers };
}

/**
 * Detect the header row, then key each non-empty data row beneath it by header.
 * All-blank rows are dropped. Mirrors `matrixToObjects`.
 */
export function matrixToRecords(rows: readonly (readonly string[])[]): ImportRecord[] {
  const clean = (rows ?? []).map((r) =>
    Array.isArray(r) ? r.map((v) => (v == null ? '' : String(v))) : [],
  );
  if (!clean.length) return [];
  const { headerIndex, headers } = detectHeaderRow(clean);
  return clean
    .slice(headerIndex + 1)
    .filter((r) => r.some((c) => String(c ?? '').trim() !== ''))
    .map((r) => {
      const obj: ImportRecord = {};
      headers.forEach((h, idx) => {
        obj[h] = r[idx] ?? '';
      });
      return obj;
    });
}

// --- Alias map + field resolution ------------------------------------------

/**
 * Canonical field → ordered alias list. The first alias present in the record
 * wins (matched header-normalised). Lifted faithfully from the monolith's
 * `buildTransactionFromImport` `getField(...)` calls, plus the `raw` map's
 * extra columns, so real-world spreadsheet headings resolve.
 */
export const ALIASES: Record<string, readonly string[]> = {
  Dataset: ['Dataset', 'Data Set', 'Type'],
  'Reference No': [
    'Reference No',
    'Reference',
    'Contract Number',
    'PO Number',
    'Task Order',
    'Transaction Reference',
    'Contract No',
    'PO No',
  ],
  Year: ['Year', 'Financial Year'],
  Period: ['Period', 'Quarter'],
  Division: ['Division', 'Department'],
  Supplier: ['Supplier', 'Vendor', 'Supplier Number and Name', 'Supplier Name/Vendor Number'],
  'CSD Number': ['CSD Number', 'CSD'],
  Value: ['Value', 'Value (R)', 'Amount', 'Target Value', 'Value of Contract/PO'],
  Mechanism: ['Mechanism', 'Procurement Mechanism'],
  Link: ['Link', 'Links', 'OpenText Link'],
  'Gaps Status': ['Gaps Status', 'Status', 'Statu'],
  'Compliance Level': [
    'Compliance Level',
    'Compliance level based on Updated Findings (Strict Rule)',
  ],
  'Self-declaration Listing': ['Self-declaration Listing', 'Self Declaration Listing'],
  'Initial Findings': ['Initial Findings', 'Initial Finding'],
  'Updated Findings': ['Updated Findings', 'Updated findings', 'Findings', 'Finding'],
  'Reviewed Y/N': ['Reviewed Y/N', 'Reviewed'],
  'On the Dashboard': ['On the Dashboard'],
  // (The old 'Panel Y/N' canonical was dead — nothing ever resolved it, and the
  // alias itself already lives under 'Panel Release' below.)
  // New bulk-upload template columns (owner feedback points 3 + 4).
  OU: ['OU', 'Operating Unit'],
  'Contract Number': ['Contract Number', 'Contract No'],
  'Panel Release': ['Panel Release(Y/N)', 'Panel Release', 'Panel Y/N'],
  'Link Provided': ['link provided', 'Link Provided'],
  'Link Issue': ['Issue with links', 'Link Issue', 'Issue with link'],
  // Grey working-paper header fields (R3 #1). BU ≈ the bulk template's OU
  // column; Brief Description ≈ Description of Goods; Buyer ≈ Buyer Name. The
  // last three are normally reviewer-entered from OpenText, but the aliases let
  // a future upload column populate them automatically.
  BU: ['BU', 'Business Unit', 'OU'],
  'Brief Description': ['Brief Description', 'Description of Goods', 'Description'],
  Buyer: ['Buyer / Division', 'Buyer Name', 'Buyer'],
  'PR Number': ['PR number', 'PR No', 'Purchase Requisition'],
  'Procurement Manager': ['Procurement Manager'],
  'End-User': ['End-User', 'End User'],
};

/**
 * Resolve a canonical field's value from a record via its alias list — the
 * first alias present (header-normalised match) wins; absent → ''. Mirrors the
 * monolith's `getField`, but keyed by canonical name for call-site clarity.
 * Throws for an unknown canonical field (guards typos at the call site).
 */
export function resolveField(record: ImportRecord, canonical: string): string {
  const aliases = ALIASES[canonical];
  if (!aliases) {
    throw new Error(`Unknown canonical import field: "${canonical}".`);
  }
  for (const alias of aliases) {
    const wanted = normalizeHeader(alias);
    const hit = Object.keys(record).find((k) => normalizeHeader(k) === wanted);
    if (hit !== undefined) {
      const value = String(record[hit] ?? '').trim();
      if (value !== '') return value;
    }
  }
  return '';
}

// --- Findings ---------------------------------------------------------------

/**
 * Split a findings cell into one finding per line / numbered item. Mirrors the
 * monolith's `splitFindings`: break on newlines and before `N.`/`N)` markers.
 */
export function splitFindings(text: string): string[] {
  const raw = String(text ?? '').trim();
  if (!raw) return [];
  const parts = raw
    .split(/\n+|\r+|(?=\s*\d+[.)]\s)/)
    .map((x) => x.trim())
    .filter(Boolean);
  return parts.length ? parts : [raw];
}

/**
 * Build a {@link FindingInsertInput} from one finding line. Classifies the text
 * through the shared domain classifier (like the capture flow) for the
 * suggested compliance + anchors; an explicit Compliance Level override on the
 * row, when present, becomes the `finalCompliance`. `source` is "Imported file"
 * to match the monolith's `createFindingFromText`.
 */
export function findingFromText(
  text: string,
  tx: { mechanism?: string; value?: number },
  complianceOverride: string,
): FindingInsertInput {
  const auto = classifyFinding(text, { mechanism: tx.mechanism, value: tx.value });
  return {
    text,
    source: 'Imported file',
    legacyStatus: '',
    anchors: auto.anchors,
    suggestedCompliance: auto.level,
    finalCompliance: complianceOverride || auto.level,
    rationale: '',
    confidence: auto.confidence,
    route: auto.route,
    matchedRules: auto.matchedRules,
    effectiveStatus: '',
    reviewNotes: '',
    seeded: false,
  };
}

// --- Merge-preserve on re-import --------------------------------------------

/**
 * The set of `source` markers (normalised) that identify an import-derived
 * finding. A finding bearing one of these is "owned" by the importer and may be
 * superseded on a re-import; anything else is analyst/CES-captured work and is
 * preserved. Both the current marker (`Imported file`) and the legacy marker
 * (`Bulk upload`) are covered — lifted from the monolith's reconcile filter
 * (git 6810c83:index.html ~1291): `f.source === 'Bulk upload' || f.source ===
 * 'Imported file'`.
 */
const IMPORT_SOURCE_SET: ReadonlySet<string> = new Set(['imported file', 'bulk upload']);

/** True when a finding's `source` marks it as import-derived (case-insensitive). */
function isImportSourced(source: string | null | undefined): boolean {
  return IMPORT_SOURCE_SET.has(String(source ?? '').trim().toLowerCase());
}

/**
 * The subset of {@link FindingInsertInput} fields any finding-like value (a
 * {@link FindingInsertInput} or a read-projected finding) must expose so it can
 * be re-shaped into a {@link FindingInsertInput} for re-insertion. `text` is the
 * only required field; everything else is optional.
 */
type FindingLike = {
  text: string;
  source?: string | null;
  legacyStatus?: string | null;
  anchors?: readonly string[] | null;
  suggestedCompliance?: string | null;
  finalCompliance?: string | null;
  rationale?: string | null;
  confidence?: string | null;
  route?: string | null;
  matchedRules?: readonly string[] | null;
  effectiveStatus?: string | null;
  reviewNotes?: string | null;
  seeded?: boolean | null;
  referenceNo?: string | null;
};

/** Re-shape any finding-like value into a clean {@link FindingInsertInput}. */
function toFindingInsert(f: FindingLike): FindingInsertInput {
  return {
    text: f.text,
    source: f.source ?? undefined,
    legacyStatus: f.legacyStatus ?? undefined,
    anchors: f.anchors ? [...f.anchors] : undefined,
    suggestedCompliance: f.suggestedCompliance ?? undefined,
    finalCompliance: f.finalCompliance ?? undefined,
    rationale: f.rationale ?? undefined,
    confidence: f.confidence ?? undefined,
    route: f.route ?? undefined,
    matchedRules: f.matchedRules ? [...f.matchedRules] : undefined,
    effectiveStatus: f.effectiveStatus ?? undefined,
    reviewNotes: f.reviewNotes ?? undefined,
    seeded: f.seeded ?? undefined,
  };
}

/**
 * Merge a transaction's CURRENT findings with the findings derived from this
 * import, so a re-import refreshes only the import-sourced lines and never
 * destroys analyst/CES-captured work.
 *
 * The result is `[ preserved analyst findings, …, new import findings ]`:
 *   • EXISTING findings whose `source` is NOT import-sourced (analyst/CES work,
 *     incl. blank/absent source) are kept, re-shaped to {@link FindingInsertInput}.
 *   • EXISTING import-sourced findings (`Imported file` / `Bulk upload`, any
 *     case) are dropped — they are superseded by this import.
 *   • The new import findings are appended.
 *
 * PURE: no Firebase, no React. The caller (ImportPage) hands this to
 * `replaceFindings(referenceNo, merged)`, so `replaceFindingsForTx` (the
 * delete-then-reinsert connector) writes the union — the analyst findings are
 * re-inserted alongside the refreshed import ones rather than deleted.
 */
export function mergeImportFindings(
  existingFindings: readonly FindingLike[],
  newImportFindings: readonly FindingInsertInput[],
): FindingInsertInput[] {
  const preserved = (existingFindings ?? [])
    .filter((f) => !isImportSourced(f.source))
    .map(toFindingInsert);
  return [...preserved, ...newImportFindings];
}

// --- Row → import payload ---------------------------------------------------

/**
 * Turn one keyed record into a transaction-upsert payload + finding inputs.
 * Returns `null` when the row carries no usable reference (it should be skipped,
 * never silently dropped — the caller reports it). Value is parsed via
 * `parseRand`; dataset via the shared `inferDataset`; gaps status canonicalised;
 * findings are taken from Updated Findings (falling back to Initial Findings),
 * split into lines and classified. Unknown columns are ignored.
 */
export function rowToImport(record: ImportRecord): ImportRow | null {
  const referenceNo = resolveField(record, 'Reference No');
  if (!referenceNo) return null;

  // Yes/No → boolean; blank/unknown leaves the field unset so it never
  // overwrites with a guess (owner feedback points 3 + 4).
  const yesNo = (v: string): boolean | undefined => {
    const s = v.trim().toLowerCase();
    if (/^(y|yes|true|1)$/.test(s)) return true;
    if (/^(n|no|false|0)$/.test(s)) return false;
    return undefined;
  };
  // Panel Release is resolved BEFORE the dataset: R6 makes a panel release its
  // own dataset bucket, and the flag outranks the Dataset cell (Lindiwe's sheet
  // types those rows "Contracts" and marks them only in this column).
  const panel = yesNo(resolveField(record, 'Panel Release'));
  const datasetText = resolveField(record, 'Dataset');
  const dataset = inferDataset(datasetText, referenceNo, panel);
  const valueRaw = resolveField(record, 'Value');
  // A blank Value cell parses to null (NOT 0) — see the payload note below.
  const value = parseRand(valueRaw);
  const mechanism = resolveField(record, 'Mechanism');
  const gapsStatusRaw = resolveField(record, 'Gaps Status');
  const complianceLevel = resolveField(record, 'Compliance Level');
  const selfDeclaration = resolveField(record, 'Self-declaration Listing');
  const initialFindings = resolveField(record, 'Initial Findings');
  const updatedFindings = resolveField(record, 'Updated Findings');
  const link = resolveField(record, 'Link');
  const ou = resolveField(record, 'OU');
  const contractNumber = resolveField(record, 'Contract Number');
  const linkIssue = resolveField(record, 'Link Issue');
  const linkProvided = yesNo(resolveField(record, 'Link Provided'));
  // Grey working-paper header fields (R3 #1).
  const bu = resolveField(record, 'BU');
  const briefDescription = resolveField(record, 'Brief Description');
  const buyer = resolveField(record, 'Buyer');
  const prNumber = resolveField(record, 'PR Number');
  const procurementManager = resolveField(record, 'Procurement Manager');
  const endUser = resolveField(record, 'End-User');

  // WS2 (1 Jul 2026 incident): a cell the file does not carry is OMITTED from the
  // payload — never emitted as ''/0 — so the field-locked update leaves the row's
  // existing value untouched (and the skip-unchanged differ never sees a phantom
  // blank). Only referenceNo, recordLabel and dataset (inferred; floors at
  // 'Other') are unconditional. `str` folds whitespace-only cells to absent.
  const str = (v: string): string | undefined => {
    const t = v.trim();
    return t === '' ? undefined : t;
  };
  // Fold messy year / quarter spellings at the door (2026/27 → FY27, "Quarter 1"
  // → Q1); keep an unrecognised raw value so it still imports and surfaces.
  const yearRaw = str(resolveField(record, 'Year'));
  const year = yearRaw ? (normaliseFinancialYear(yearRaw) || yearRaw) : undefined;
  const periodRaw = str(resolveField(record, 'Period'));
  const period = periodRaw ? (normalisePeriod(periodRaw) || periodRaw) : undefined;
  const division = str(resolveField(record, 'Division'));
  const supplier = str(resolveField(record, 'Supplier'));
  const csdNumber = str(resolveField(record, 'CSD Number'));
  const mech = str(mechanism);
  const linkVal = str(link);
  const compliance = str(complianceLevel);
  const selfDecl = str(selfDeclaration);
  const initial = str(initialFindings);
  const updated = str(updatedFindings);
  const gapsStatus = normalizeGapStatus(gapsStatusRaw);
  const transaction: TransactionInsertInput = {
    referenceNo,
    recordLabel: 'Transaction Reference',
    dataset,
    ...(year ? { year } : {}),
    ...(period ? { period } : {}),
    ...(division ? { division } : {}),
    ...(supplier ? { supplier } : {}),
    ...(csdNumber ? { csdNumber } : {}),
    // A blank Value cell must never land as 0 (it would overwrite a real value on
    // re-import); a literal 0 still imports as 0 with a blank display.
    ...(value != null ? { value, valueDisplay: value ? formatRand(value) : '' } : {}),
    ...(mech ? { mechanism: mech } : {}),
    ...(linkVal ? { link: linkVal } : {}),
    // Four-state lifecycle at the door (alignment call): legacy spellings fold
    // to Open / In Progress / Closed / Closed by Self-Declaration; an
    // unreviewed row imports with NO gap status (omitted, not '').
    ...(gapsStatus ? { gapsStatus } : {}),
    ...(compliance ? { complianceLevel: compliance } : {}),
    ...(selfDecl ? { selfDeclaration: selfDecl } : {}),
    ...(initial ? { initialFindings: initial } : {}),
    ...(updated ? { updatedFindings: updated } : {}),
    ...(ou ? { ou } : {}),
    ...(contractNumber ? { contractNumber } : {}),
    ...(linkIssue ? { linkIssue } : {}),
    ...(panel !== undefined ? { panel } : {}),
    ...(linkProvided !== undefined ? { linkProvided } : {}),
    ...(bu ? { bu } : {}),
    ...(briefDescription ? { briefDescription } : {}),
    ...(buyer ? { buyer } : {}),
    ...(prNumber ? { prNumber } : {}),
    ...(procurementManager ? { procurementManager } : {}),
    ...(endUser ? { endUser } : {}),
  };

  // Findings: prefer Updated Findings, fall back to Initial Findings (parity
  // with processBulkRows). Each line becomes a classified finding; an explicit
  // Compliance Level on the row overrides the suggested final compliance.
  const findingsText = firstNonBlank(updatedFindings, initialFindings);
  const findings = splitFindings(findingsText).map((line) =>
    findingFromText(line, { mechanism, value: value ?? 0 }, complianceLevel),
  );

  return { transaction, findings };
}

// --- WS2: skip-unchanged + in-batch duplicate helpers ------------------------

/** Canonicalise a scalar for the import differ: blank/absent strings fold to null. */
function canonForDiff(v: unknown): unknown {
  if (v === undefined || v === null) return null;
  if (typeof v === 'string') {
    const t = v.trim();
    return t === '' ? null : t;
  }
  return v;
}

/** Numeric fold for the `value` column: blank/absent/NaN → null, else Number. */
function numForDiff(v: unknown): number | null {
  if (v === undefined || v === null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Columns the import update never WRITES on an existing row (insert-only since
 * WS2) but must still DIFF: they drive derived work — `complianceLevel` feeds
 * the finding-line grading (findingFromText's final-compliance override) and
 * `gapsStatus` the lifecycle — so a file changing only these cells must not be
 * skipped as "unchanged" (the findings replace is where the change lands).
 */
const DIFF_ONLY_IMPORT_COLUMNS = ['complianceLevel', 'gapsStatus'] as const;

/** The key isUnchangedForImport (and the ImportPage maps) accept for `existing`. */
type ImportDiffKey = (typeof IMPORT_UPDATE_COLUMNS)[number] | (typeof DIFF_ONLY_IMPORT_COLUMNS)[number];

/**
 * True when re-importing `incoming` over `existing` would change nothing the
 * import writes ({@link IMPORT_UPDATE_COLUMNS}) NOR anything the row's derived
 * findings depend on ({@link DIFF_ONLY_IMPORT_COLUMNS}) — the caller can then
 * consider skipping the row (it must ALSO check {@link importFindingsMatch}).
 *
 * Rules: an `undefined` incoming field is ignored (the field-locked mapper would
 * omit it, leaving the row untouched); strings compare with blank ≡ null ≡
 * undefined; `value` compares numerically; booleans strictly. Review/work-product
 * columns are invisible here by construction — they are not import columns, so a
 * reviewer's edits never make a row look "changed" to the import.
 */
export function isUnchangedForImport(
  existing: Partial<Record<ImportDiffKey, unknown>>,
  incoming: TransactionInsertInput,
): boolean {
  for (const col of [...IMPORT_UPDATE_COLUMNS, ...DIFF_ONLY_IMPORT_COLUMNS]) {
    const inc = incoming[col];
    if (inc === undefined) continue;
    if (col === 'value') {
      if (numForDiff(existing[col]) !== numForDiff(inc)) return false;
      continue;
    }
    if (canonForDiff(existing[col]) !== canonForDiff(inc)) return false;
  }
  return true;
}

/**
 * True when the row's IMPORT-SOURCED findings already match the file's finding
 * lines (ordered text compare; analyst/CES findings are ignored — the merge
 * preserves them regardless). The skip-unchanged guard requires this so that
 * (a) a file whose only change is its findings text still lands, and (b) a row
 * whose earlier findings replace FAILED (transaction written, findings lost)
 * heals on the natural re-run of the same file instead of short-circuiting.
 */
export function importFindingsMatch(
  existingFindings: readonly FindingLike[],
  newImportFindings: readonly FindingInsertInput[],
): boolean {
  const existingTexts = (existingFindings ?? [])
    .filter((f) => isImportSourced(f.source))
    .map((f) => (f.text ?? '').trim());
  const newTexts = (newImportFindings ?? []).map((f) => (f.text ?? '').trim());
  if (existingTexts.length !== newTexts.length) return false;
  return existingTexts.every((t, i) => t === newTexts[i]);
}

/**
 * The ONE reference-key normalisation (trim + lowercase) used by the import's
 * dedupe/diff/merge maps — six call sites keyed refs ad hoc before this.
 */
export function refKey(ref: string | null | undefined): string {
  return (ref ?? '').trim().toLowerCase();
}

/**
 * Map each normalised (trim + lowercase) referenceNo to the index of its LAST
 * occurrence in the batch. In-batch duplicates resolve last-wins: the appended
 * row is the correction (spreadsheet convention), and writing only the last
 * occurrence also stops the second write's findings-merge from working off a
 * stale cache and silently deleting the first's just-written findings. Null and
 * blank refs (unmapped rows) are skipped — they are not duplicates of each other.
 */
export function lastOccurrenceByRef(refs: ReadonlyArray<string | null | undefined>): Map<string, number> {
  const map = new Map<string, number>();
  refs.forEach((ref, index) => {
    const key = refKey(ref);
    if (key) map.set(key, index);
  });
  return map;
}

// --- Workbook reading (SheetJS) --------------------------------------------

/**
 * Read an XLSX (ArrayBuffer) or CSV (string) into a matrix of string cells.
 * SheetJS reads both formats; the first sheet that contains any non-blank cell
 * is used (mirrors the monolith's sheet-pick). `raw:false` keeps cell text as
 * displayed so numeric/percent formats survive as strings for `parseRand` etc.
 */
export function parseWorkbook(input: ArrayBuffer | string): string[][] {
  const wb =
    typeof input === 'string'
      ? XLSX.read(input, { type: 'string' })
      : XLSX.read(input, { type: 'array' });

  const sheetName =
    wb.SheetNames.find((name) => {
      const ws = wb.Sheets[name];
      const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, {
        header: 1,
        defval: '',
        blankrows: false,
      });
      return rows.some(
        (r) => Array.isArray(r) && r.some((c) => String(c ?? '').trim() !== ''),
      );
    }) ?? wb.SheetNames[0];

  if (!sheetName) return [];

  const ws = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, {
    header: 1,
    defval: '',
    blankrows: false,
    raw: false,
  });
  return rows.map((r) =>
    Array.isArray(r) ? r.map((c) => (c == null ? '' : String(c))) : [],
  );
}

/**
 * Convenience: read a workbook and map straight to keyed records (detect header
 * + key rows). The screen uses this for its preview.
 */
export function parseToRecords(input: ArrayBuffer | string): ImportRecord[] {
  return matrixToRecords(parseWorkbook(input));
}
